import { useCallback } from 'react';
import intl from 'react-intl-universal';
import { PROGRAMS_STATUS, SELECTABLE_PROGRAM_STATUSES } from 'utils/constants';
import { useDispatch } from 'react-redux';
import { showNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import { updateProgramInList } from 'state/Programs/programsSlice';
import { StatusOption } from 'Atoms/EditableTableRowCell/StatusTableRowCell/StatusTableRowCell';
import programAPI from 'state/Program/programAPI';
import { ProgramLinkedProject } from 'utils/customTypes';

interface UseUpdateProgramStatusReturn {
  handleSaveProgramStatus: (
    newValue: string,
    programId: string,
    currentStatus: string
  ) => void;
  getProgramStatuses: (projects: ProgramLinkedProject[]) => StatusOption[];
}

export const useUpdateProgramStatus = (): UseUpdateProgramStatusReturn => {
  const dispatch = useDispatch();

  const handleSaveProgramStatus = useCallback(
    async (newValue: string, programId: string, currentStatus: string) => {
      if (newValue === currentStatus) return;

      try {
        const response = await programAPI.updateProgram(programId, {
          status: newValue,
        });

        if (response.success) {
          dispatch(
            updateProgramInList({
              programId,
              updatedFields: { status: newValue },
            })
          );
        } else {
          dispatch(
            showNotification({
              notificationVariant: 'error',
              notificationTitle: intl.get('PROGRAM_PAGE.UPDATE_ERROR'),
            })
          );
        }
      } catch {
        dispatch(
          showNotification({
            notificationVariant: 'error',
            notificationTitle: intl.get('PROGRAM_PAGE.UPDATE_ERROR'),
          })
        );
      }
    },
    [dispatch]
  );

  const getProgramStatuses = useCallback(
    (projects: ProgramLinkedProject[]): StatusOption[] => {
      const cannotClose =
        projects.length > 0 &&
        projects.some(
          (project) =>
            project.status !== 'completed' &&
            project.status !== 'canceled' &&
            project.status !== 'closed'
        );

      return SELECTABLE_PROGRAM_STATUSES.map((value) => {
        const isClosedOption = value === PROGRAMS_STATUS.CLOSED;

        return {
          label: intl.get(`PROGRAMS_LIST_PAGE.STATUS.${value.toUpperCase()}`),
          value,
          disabled: isClosedOption && cannotClose,
          message:
            isClosedOption && cannotClose
              ? intl.get('PROGRAM_PAGE.CLOSED_TOOLTIP')
              : undefined,
        };
      });
    },
    []
  );

  return {
    handleSaveProgramStatus,
    getProgramStatuses,
  };
};
